import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {ProcessesPluginMetadataEditorItem} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {ApplyFlowchartChangesFn} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import FlowchartContextPanel, {
  getStyleForSidepanelSize,
  PanelSide,
  ContextPanelContent,
  SidepanelSize,
  sizeToWidth,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar, {
  FLOWCHART_SIDEPANEL_Z_INDEX,
} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {SidepanelType} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelManager';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import AddNodeSidepanelContent from './left-sidepanel/AddNodeSidepanelContent';
import FlowchartTransitionsSidepanelContent from './left-sidepanel/FlowchartTransitionsSidepanelContent';
import {TransitionModalResult} from './left-sidepanel/transition-modal/extractTransitionFromDraftState';
import {RootAndOutputNodeConfig, UserFacingNodeTypes} from './processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  FlowchartOptions,
} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';
import {ProcessTaskDefinitionsResult} from './useAllSchemasById';

export const LEFT_SIDEPANEL_SIZE = SidepanelSize.EXTRA_SMALL;
export const LEFT_SIDEPANEL_WIDTH = sizeToWidth[LEFT_SIDEPANEL_SIZE] as number;

export interface LeftSidepanelContent {
  icon: BenchlingIconName;
  name: string;
  content: JSX.Element;
  width: SidepanelSize;
}

export interface ProcessFlowchartMetadataEditorProps {
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  flowchartTypeName: string;
  /** All task schemas that are part of this flowchart (from nodes on the canvas). */
  flowchartTaskSchemas?: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
}

export interface LeftSidepanelProps {
  allowedNodeTypes: Array<UserFacingNodeTypes>;
  taskDefinitionsResult: ProcessTaskDefinitionsResult;
  options: FlowchartOptions;
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  leftSidepanelRef?: React.RefObject<HTMLDivElement>;
  metadataSidepanel?: ProcessesPluginMetadataEditorItem;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  setPlaceholderNodeBeingAdded: (value: string | null) => void;
  /** Maps PTDV client ID to the task schema ID it belongs to */
  ptdvIdToTaskSchemaId: Record<string, string>;
  allSchemasById: SchemaById;
  flowchartTaskDefinitionId: string;
  flowchartConfigId: string;
  rootAndOutputNodeConfig?: RootAndOutputNodeConfig;
}

/**
 * Builds REPLACE node changes for main canvas nodes whose PTDV specs were modified in the
 * transition modal. Matches by PTDV client ID and applies the updated spec and sections.
 */
function buildNodeReplacementsFromModalData(
  canvasNodes: ReadonlyArray<StaticFlowchartNodeData>,
  updatedNodeDataByPTDVId: Record<string, TaskSchemaStaticNodeData>
): Array<FlowchartNodeChange> {
  const nodeChanges: Array<FlowchartNodeChange> = [];
  for (const canvasNode of canvasNodes) {
    if (!isStaticTaskSchemaNodeData(canvasNode)) {
      continue;
    }
    const ptdvId = canvasNode.taskDefinitionVersionSpec.clientId;
    const modalNode = updatedNodeDataByPTDVId[ptdvId];
    if (modalNode == null) {
      continue;
    }
    nodeChanges.push({
      type: FlowchartNodeChangeType.REPLACE,
      id: canvasNode.id,
      data: {
        ...canvasNode,
        taskDefinitionVersionSpec: modalNode.taskDefinitionVersionSpec,
        sections: modalNode.sections,
        nodeName: modalNode.nodeName,
      },
    });
  }
  return nodeChanges;
}

export default function ProcessFlowchartConfigEditorLeftSidepanel(props: LeftSidepanelProps): JSX.Element {
  const [selectedSidebarIconIndex, setSelectedSidebarIconIndex] = useState<number | null>(null);
  // Track the previous tab index for analytics
  const previousTabIndexRef = useRef<number | null>(null);
  const locked = ReactFlowLockedContext.useContext() ?? false;

  const sidepanelManager = useFlowchartSidepanelManagerContext();
  const {getEffectiveState} = sidepanelManager;

  const {openSidepanelType: currentOpenSidepanelType} = getEffectiveState();
  const isOpen = useMemo(
    () => currentOpenSidepanelType === SidepanelType.FLOWCHART_SETTINGS,
    [currentOpenSidepanelType]
  );

  useEffect(() => {
    if (!isOpen) {
      // If the sidepanel is closed due to the sidepanel state manager instead of a specific
      // action to close the left sidepanel, clear selected sidebar icon index and previous tab ref
      setSelectedSidebarIconIndex(null);
      previousTabIndexRef.current = null;
    }
  }, [isOpen, setSelectedSidebarIconIndex]);

  // Handle sidebar icon selection changes from FlowchartSidebar.
  const handleSetSelectedIconIndex = useCallback(
    (index: number | null) => {
      if (index == null && selectedSidebarIconIndex != null && isOpen) {
        // User clicked on the already-selected icon to toggle it off
        sidepanelManager.changeSidepanel({
          sidepanelChange: {
            sidepanel: null,
          },
        });
      }
      setSelectedSidebarIconIndex(index);
    },
    [selectedSidebarIconIndex, isOpen, sidepanelManager]
  );

  const {applyFlowchartChanges} = props;

  const handleEditTransition = useCallback(
    ({transition: updatedTransition, updatedNodeDataByPTDVId}: TransitionModalResult) => {
      applyFlowchartChanges((currentState) => ({
        transitionChanges: [
          {type: 'remove', id: updatedTransition.id},
          {type: 'add', transition: updatedTransition},
        ],
        nodeChanges: buildNodeReplacementsFromModalData(
          currentState.staticFlowchart.nodesStaticData,
          updatedNodeDataByPTDVId
        ),
      }));
    },
    [applyFlowchartChanges]
  );

  const handleCreateTransition = useCallback(
    ({transition: newTransition, updatedNodeDataByPTDVId}: TransitionModalResult) => {
      applyFlowchartChanges((currentState) => ({
        transitionChanges: [{type: 'add', transition: newTransition}],
        nodeChanges: buildNodeReplacementsFromModalData(
          currentState.staticFlowchart.nodesStaticData,
          updatedNodeDataByPTDVId
        ),
      }));
    },
    [applyFlowchartChanges]
  );

  const handleDeleteTransition = useCallback(
    (transitionId: string) => {
      applyFlowchartChanges(() => ({
        transitionChanges: [{type: 'remove', id: transitionId}],
      }));
    },
    [applyFlowchartChanges]
  );

  const handleGoToFlowchart = useCallback(async () => {
    const success = await sidepanelManager.changeSidepanel({
      sidepanelChange: {
        sidepanel: null,
      },
    });
    if (success) {
      setSelectedSidebarIconIndex(null);
      previousTabIndexRef.current = null;
    }
  }, [sidepanelManager]);

  const sidepanelContents: Array<LeftSidepanelContent> = [];

  if (
    !locked &&
    props.flowchartEditorConfigurator.hasFeature(ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL)
  ) {
    sidepanelContents.push({
      icon: 'plus',
      name: 'Add to flowchart',
      content: (
        <AddNodeSidepanelContent
          allowedNodeTypes={props.allowedNodeTypes}
          taskDefinitionsResult={props.taskDefinitionsResult}
          applyFlowchartChanges={props.applyFlowchartChanges}
          options={props.options}
          rootTaskSchema={props.rootTaskSchema}
          flowchartEditorConfigurator={props.flowchartEditorConfigurator}
          reactFlowWrapper={props.reactFlowWrapper}
          setPlaceholderNodeBeingAdded={props.setPlaceholderNodeBeingAdded}
          rootAndOutputNodeConfig={props.rootAndOutputNodeConfig}
        />
      ),
      width: SidepanelSize.EXTRA_SMALL,
    });
  }

  const flowchartTaskSchemas = useMemo(() => {
    if (props.taskDefinitionsResult == null) {
      return undefined;
    }
    const transitionPtdvIds = Object.values(props.flowchartConfigMetadata.transitions).flatMap((t) => [
      ...t.sourcePTDVIds,
      t.targetPTDVId,
    ]);
    const uniqueSchemaIds = new Set<string>([
      props.rootTaskSchema.id,
      ...transitionPtdvIds
        .map((id) => props.ptdvIdToTaskSchemaId[id])
        .filter((id): id is string => id != null),
    ]);
    return props.taskDefinitionsResult
      .filter((def) => uniqueSchemaIds.has(def.latestVersion.processTaskSchema.id))
      .map((def) => def.latestVersion.processTaskSchema);
  }, [
    props.taskDefinitionsResult,
    props.ptdvIdToTaskSchemaId,
    props.rootTaskSchema,
    props.flowchartConfigMetadata.transitions,
  ]);

  if (props.metadataSidepanel) {
    sidepanelContents.push({
      icon: props.metadataSidepanel.icon,
      name: props.metadataSidepanel.name,
      content: (
        <props.metadataSidepanel.editorComponent
          flowchartTaskSchema={props.rootTaskSchema}
          flowchartConfigMetadata={props.flowchartConfigMetadata}
          applyFlowchartChanges={props.applyFlowchartChanges}
          flowchartTypeName={props.flowchartEditorConfigurator.flowchartTypeName}
          flowchartTaskSchemas={flowchartTaskSchemas}
        />
      ),
      width: props.metadataSidepanel.width,
    });
  }

  const allowsTransitionsSidepanel =
    BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_TRANSITIONS_SIDEPANEL_IN_HORIZONTAL_FLOWCHART &&
    props.flowchartEditorConfigurator.hasFeature(
      ProcessFlowchartPlatformFeatureType.SHOW_FLOWCHART_TRANSITIONS_SIDEPANEL
    );

  if (allowsTransitionsSidepanel) {
    sidepanelContents.push({
      icon: 'arrows-turn-right',
      name: 'Transitions',
      content: (
        <FlowchartTransitionsSidepanelContent
          metadataTransitions={props.flowchartConfigMetadata.transitions}
          ptdvIdToTaskSchemaId={props.ptdvIdToTaskSchemaId}
          taskDefinitionsResult={props.taskDefinitionsResult}
          rootTaskSchema={props.rootTaskSchema}
          flowchartEditorConfigurator={props.flowchartEditorConfigurator}
          allSchemasById={props.allSchemasById}
          flowchartTaskDefinitionId={props.flowchartTaskDefinitionId}
          flowchartConfigId={props.flowchartConfigId}
          onEditTransition={handleEditTransition}
          onCreateTransition={handleCreateTransition}
          onDeleteTransition={handleDeleteTransition}
          onGoToFlowchart={handleGoToFlowchart}
          isLocked={locked}
        />
      ),
      width: SidepanelSize.FULL,
    });
  }

  if (sidepanelContents.length === 0) {
    return <></>;
  }

  const selectedSidepanelContent =
    selectedSidebarIconIndex != null ? sidepanelContents[selectedSidebarIconIndex] : null;

  return (
    <div
      ref={props.leftSidepanelRef}
      className={css(
        styles.leftSidepanelContainer,
        isOpen &&
          selectedSidepanelContent?.width === SidepanelSize.FULL &&
          getStyleForSidepanelSize(SidepanelSize.FULL)
      )}
    >
      <FlowchartSidebar
        icons={sidepanelContents.map((content, index) => ({
          name: content.icon,
          onClick: async () => {
            const success = await sidepanelManager.changeSidepanel({
              sidepanelChange: {
                sidepanel: SidepanelType.FLOWCHART_SETTINGS,
                nodeId: null,
                fieldId: null,
              },
            });
            if (!success) {
              setSelectedSidebarIconIndex(null);
              previousTabIndexRef.current = null;
              return;
            }
            setSelectedSidebarIconIndex(index);
            previousTabIndexRef.current = index;
          },
          tooltip: content.name,
          tooltipPlacement: FloatingPlacement.RIGHT,
        }))}
        selectedIconIndex={selectedSidebarIconIndex}
        setSelectedIconIndex={handleSetSelectedIconIndex}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        size={selectedSidepanelContent?.width ?? LEFT_SIDEPANEL_SIZE}
        sidepanelName={selectedSidepanelContent?.name ?? ''}
        isOpen={isOpen}
        onClose={async () => {
          const success = await sidepanelManager.changeSidepanel({
            sidepanelChange: {
              sidepanel: null,
            },
          });
          if (success) {
            setSelectedSidebarIconIndex(null);
            previousTabIndexRef.current = null;
          }
        }}
        flowchartType={props.flowchartEditorConfigurator.flowchartTypeName}
      >
        <ContextPanelContent>{selectedSidepanelContent?.content}</ContextPanelContent>
      </FlowchartContextPanel>
    </div>
  );
}

const styles = StyleSheet.create({
  leftSidepanelContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    height: '100%',
    zIndex: FLOWCHART_SIDEPANEL_Z_INDEX,
  },
});
